import styled from 'foundations-theming/utils';
import { useEffect, useRef } from 'react';
import { useDragPreviewContext } from './DragPreview/DragPreviewContext';
import { createPortal } from 'react-dom';
import { jsx as _jsx } from "react/jsx-runtime";
const ShadowedDragPreview = styled.div.withConfig({
  displayName: "DragPreview__ShadowedDragPreview"
})([".hs-private.hs-private.hs-private &{position:absolute;background-color:", ";}"], ({
  theme
}) => theme.color['fill-tertiary-pressed']);
const useScrollPosition = () => {
  const scrollPositionRef = useRef({
    initialLeft: window.scrollX,
    initialTop: window.scrollY,
    left: 0,
    top: 0
  });
  useEffect(() => {
    const handleScroll = () => {
      scrollPositionRef.current.left = window.scrollX - scrollPositionRef.current.initialLeft;
      scrollPositionRef.current.top = window.scrollY - scrollPositionRef.current.initialTop;
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  return scrollPositionRef.current;
};
const useElementSizeAndPosition = element => {
  const elementSizeRef = useRef({
    width: 0,
    height: 0,
    left: 0,
    top: 0
  });
  useEffect(() => {
    if (element) {
      const rect = element.getBoundingClientRect();
      elementSizeRef.current.left = rect.left;
      elementSizeRef.current.top = rect.top;
      elementSizeRef.current.width = rect.width;
      elementSizeRef.current.height = rect.height;
    }
  }, [element]);
  return elementSizeRef.current;
};
/**
 * Renders a shadowed drag preview of the element to be dragged, with the same
 * size and position as the dragged element. The dragged element is updated to
 * then follow the cursor position when the cursor is moved.
 */
const DragPreview = ({
  scale = 1,
  element,
  differenceFromInitialOffset
}) => {
  const {
    dragPreviewZIndex: zIndex,
    shadowedDragPreviewRef
  } = useDragPreviewContext();
  const {
    width,
    height,
    left: elementLeft,
    top: elementTop
  } = useElementSizeAndPosition(element);
  const {
    left: scrollLeft,
    top: scrollTop,
    initialLeft: initialScrollLeft,
    initialTop: initialScrollTop
  } = useScrollPosition();

  // Updates the dragged element to follow the cursor position when the cursor
  // is moved. It's adjusted to account for a cursor offset and scroll position.
  useEffect(() => {
    if (element && differenceFromInitialOffset) {
      const x = (differenceFromInitialOffset.x + scrollLeft) / scale;
      const y = (differenceFromInitialOffset.y + scrollTop) / scale;
      element.style.transform = `translate(${x}px, ${y}px)`;
      element.style.zIndex = zIndex;
      element.style.opacity = '0.8';
    }
    return () => {
      if (element) {
        element.style.removeProperty('z-index');
        element.style.removeProperty('transform');
        element.style.removeProperty('opacity');
      }
    };
  }, [element, scrollLeft, scrollTop, zIndex, scale, differenceFromInitialOffset]);
  const styles = {
    width: `${width}px`,
    height: `${height}px`,
    left: `${elementLeft + initialScrollLeft}px`,
    top: `${elementTop + initialScrollTop}px`
  };
  return /*#__PURE__*/createPortal(
  /*#__PURE__*/
  // @bend-expect trellis-tokenized
  _jsx("div", {
    className: "hs-private",
    children: /*#__PURE__*/_jsx(ShadowedDragPreview, {
      style: styles,
      "data-test-id": "drag-preview"
    })
  }), shadowedDragPreviewRef.current);
};
export default DragPreview;